import {forwardRef} from 'react';
import {FormProvider} from 'react-hook-form';
import {BrandSection, CreateBrand} from '@tinct/core-api-models';
import {Button, Card, CardContent, CardFooter} from '@tinct/web-components';
import {useCreateOrganizationBrandForm} from '../hooks/use-create-organization-brand-form';
import {createConnectedInputHelpers} from "@tinct/web-form";

/** The brand sections this form edits: its save takes these from the form, the others from the loaded version. */
const VISUAL_IDENTITY_SECTIONS: readonly BrandSection[] = ['visualIdentity'];

type UpdateVisualIdentityFormProps = {
  organizationId: string | null;
  onSuccess?: () => void;
  hideFooter?: boolean;
};

export const UpdateVisualIdentityForm = forwardRef<HTMLFormElement, UpdateVisualIdentityFormProps>(({
  hideFooter = false,
  organizationId,
  onSuccess,
}, ref) => {
  const { form, isReady, handleSubmit, isLoading } = useCreateOrganizationBrandForm({
    organizationId,
    sections: VISUAL_IDENTITY_SECTIONS,
    onSuccess,
  });

  const { ControlledTokenInput, ControlledColorTokenInput, ControlledColorPickerInput } = createConnectedInputHelpers<CreateBrand>();

  // The <form> (and so the ref a funnel submits through) only exists once the
  // loaded values are in it: nothing can submit the empty defaults.
  if (!isReady) {
    return <div>Loading...</div>;
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-8">
        <form ref={ref} onSubmit={handleSubmit}>
          <Card className="rounded-2xl gap-0 py-0">
            <CardContent className="px-6 pt-2">
              <div className="flex flex-col divide-y">
                <ControlledColorPickerInput
                  className="py-6"
                  name="visualIdentity.primaryColor"
                  label="Primary Color"
                  direction="horizontal"
                />
                <ControlledColorTokenInput
                  className="py-6"
                  name="visualIdentity.secondaryColors"
                  label="Secondary Colors"
                  direction="horizontal"
                />
                <ControlledTokenInput
                  className="py-6"
                  name="visualIdentity.imageryStyle"
                  label="Imagery Style"
                  direction="horizontal"
                  placeholder="Imagery Style..."
                />
              </div>
            </CardContent>
            {!hideFooter && (
              <CardFooter className="border-t justify-end py-6">
                <Button type="submit" isLoading={isLoading}>Save</Button>
              </CardFooter>
            )}
          </Card>
        </form>
      </div>
    </FormProvider>
  );
});

UpdateVisualIdentityForm.displayName = 'UpdateVisualIdentityForm';
